import { getHelper } from '@/devtools/utils';
import { SIMPLE_CHOICE } from '@/features/ChoicesLists/Simple/types';
import { VersicherungsnehmerPropertyContractSchema } from '@/features/Clients/Schemas/Versicherungsnehmer';
import type { ComparisonDetail } from '@/features/Comparison/Components/types';
import type { SpartenConfig } from '@/features/Sparten/types';
import { formatCurrency } from '@/utils/formatters/euro';
import { formatYears } from '@/utils/formatters/years';

import { complexSelectionFormatter } from '@/components/form-elements/ComplexSelect/helper';
import { ENTITY_TYPE } from '@/features/EntityManager/types';
import { SEARCH_DATA_CONTEXT } from '@/features/Global/types';
import { rlvInputDehydrationDefinitions } from '@/features/Sparten/rlv/inputDehydration';
import {
  InputStore,
  useRlvInputStore,
} from '@/features/Sparten/rlv/useInputStore';
import { keepDataFromQuoteBaseProvidentDefinition } from '@/features/Sparten/shared/provident/keepDataFromQuoteBaseProvidentDefinition';
import { providentQuotePreInputMergeHandler } from '@/features/Sparten/shared/provident/quotePreInputMergeHandler';
import { SORTABLE_ATTRIBUTES } from '@/features/Tariffs/sorting';
import { dehydrateRequestData } from '@/utils/dehydrateRequestData/dehydrateRequestData';
import { formatChoice } from '@/utils/formatters/simpleChoice';
import { useStorableState } from '@/utils/useStorableState/useStorableState';
import image from './assets/bg.jpg';
import { rlvVersicherungsangabenConfig } from '@/features/Sparten/rlv/versicherungsangabenConfig';

export const config: SpartenConfig = {
  component: () => import('@/views/Base/Layout.vue'),
  path: '/rlv',
  props: {
    name: 'Risikolebensversicherung',
    sparte: 'rlv',
    image,
  },
  general: {
    distribution: {
      channel: false,
      pooling: false,
    },

    maxSimultaneousCount: 3,

    versicherungsangabenConfig: rlvVersicherungsangabenConfig,

    clientFields: {
      versicherungsnehmer: {
        geburtsdatum: 'geburtsdatum',
      },
      versichertePerson: {
        geburtsdatum: 'geburtsdatum',
        anteilTaetigkeitBuero: 'anteilTaetigkeitBuero',
        anzahlKinder: 'anzahlKinder',
        gewicht: 'gewicht',
        groesse: 'groesse',
        berufsgruppe: 'berufsgruppe',
        hoechsterBildungsabschluss: 'hoechsterBildungsabschluss',
        familienstand: 'familienstand',
        personalverantwortung: 'personalverantwortung',
        nichtraucher: 'nichtraucher',
      },
    },

    calculationRelevantEntityFields: {
      [ENTITY_TYPE.CLIENT]: {
        versicherungsnehmer: ['geburtsdatum'],
        versichertePerson: [
          'geburtsdatum',
          'anteilTaetigkeitBuero',
          'anzahlKinder',
          'gewicht',
          'groesse',
          'berufsgruppe',
          'hoechsterBildungsabschluss',
          'familienstand',
          'personalverantwortung',
          'nichtraucher',
        ],
      },
    },

    profileFields: [
      'zahlweise',
      'beitragsdynamik',
      'niedrigerAnfangsbeitrag',
      'versicherungssumme',
      'leistungsverlauf',
      'ueberschusssystem',
    ],

    getSortableAttributes: () => [
      SORTABLE_ATTRIBUTES.ERFUELLUNGSGRAD,
      SORTABLE_ATTRIBUTES.GESELLSCHAFT,
      SORTABLE_ATTRIBUTES.MAX_MOEGLICHER_BEITRAG,
      SORTABLE_ATTRIBUTES.TARIFNAME,
    ],

    buildInputDataObject: (context: SEARCH_DATA_CONTEXT, dehydrate = false) => {
      const state = useRlvInputStore().$state as InputStore;

      const preparedData = useStorableState().create(state);

      if (context === SEARCH_DATA_CONTEXT.TARIFF && dehydrate) {
        return dehydrateRequestData(
          preparedData,
          rlvInputDehydrationDefinitions,
        );
      }

      return preparedData;
    },

    buildContractDataObject: (state) => state,

    keepDataFromQuote: {
      clients: {
        versicherungsnehmer: [...keepDataFromQuoteBaseProvidentDefinition],
        versichertePerson: [...keepDataFromQuoteBaseProvidentDefinition],
      },
    },

    quotePreInputMergeCallback: (state) =>
      providentQuotePreInputMergeHandler(state),

    comparison: {
      getDetails: () => {
        const map = new Map<string, ComparisonDetail>();

        map.set('betrag', {
          label: 'zu zahlender Beitrag',
          format: formatCurrency,
        });

        map.set('maxMoeglicherBeitrag', {
          label: 'max. möglicher Beitrag',
          format: formatCurrency,
        });

        return map;
      },
      serviceFields: [
        {
          label: 'Versicherungssumme',
          fieldPath: 'versicherungssumme',
          formatter: (val: number | string) => formatCurrency(val, true),
        },
        {
          label: 'Endalter',
          fieldPath: 'versicherungsendalter',
          formatter: formatYears,
          inputFieldAsFallback: true,
        },
        {
          label: 'Zahlweise',
          fieldPath: 'zahlweise',
          formatter: formatChoice(SIMPLE_CHOICE.PAYMENT_PERIOD),
        },
        {
          label: 'Beruf',
          inputFieldPath: 'berufComplex',
          fieldPath: 'berufsbezeichnung',
          formatter: complexSelectionFormatter,
        },
      ],
    },

    tariffComponent: () =>
      import(
        '@/features/Sparten/rlv/components/TariffDetails/TariffDetails.vue'
      ),

    baseTariffComponent: () =>
      import('@/features/Sparten/rlv/components/TariffDetails/BaseTariff.vue'),

    devtools: {
      directAccess: {
        input: {
          icon: 'edit',
          setup: async () => getHelper('rlv').resetState(),
        },
        offers: {
          icon: 'list',
          setup: async () => getHelper('rlv').selectAllFeatures(),
        },
        comparison: {
          icon: 'compare_arrows',
          setup: async () => getHelper('rlv').loadTariffs(),
        },
        contract: {
          icon: 'description',
          setup: async () => getHelper('rlv').showContract(),
        },
      },
    },

    contractVnSchema: () => VersicherungsnehmerPropertyContractSchema,
  },

  steps: {
    input: {
      component: () => import('@/features/Sparten/rlv/views/InputView.vue'),
    },

    offers: {
      component: () => import('@/features/Sparten/rlv/views/Tariffs.vue'),
    },

    comparison: {
      component: () => import('@/features/Sparten/rlv/views/Comparison.vue'),
      meta: {
        showContractButton: true,
      },
    },

    contract: {
      component: () => import('@/features/Sparten/rlv/views/Contract.vue'),
    },

    completion: {
      component: () => import('@/views/Layouts/Completion/Completion.vue'),
    },
  },
};
